import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'

import {openAlert} from '../../../../../_metronic/elements/NotificationAlert'
import {useErrorHandler} from '../../../../../_metronic/helpers/ErrorHandler'
import {KTSVG} from '../../../../../_metronic/helpers'
import {useIsMobile} from '../../../../shared/responsive'
import * as ReportService from '../services/ReportService'
import {useEconomicReportContext} from './EconomicReportContext'

const OPEN_STATUSES = new Set(['completed', 'completed_with_warnings'])
const DELETABLE_STATUSES = new Set(['completed', 'completed_with_warnings', 'error'])

const Report = ({report}) => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const {errorHandler} = useErrorHandler()
  const {load} = useEconomicReportContext()
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const canOpenReport = OPEN_STATUSES.has(report.status)
  const canDeleteFromList = DELETABLE_STATUSES.has(report.status)

  const onClick = () => {
    if (!canOpenReport) {
      openAlert('info', t('reports:economic.list.still_creating'))
      return
    }

    navigate(`/gis/reports/economic/view/${report.id}`)
  }

  const onKeyDown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()
    onClick()
  }

  const stopAction = (event) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const requestDelete = (event) => {
    stopAction(event)
    setConfirmDelete(true)
  }

  const cancelDelete = (event) => {
    stopAction(event)
    setConfirmDelete(false)
  }

  const deleteReport = (event) => {
    stopAction(event)
    setDeleting(true)

    errorHandler(ReportService.deleteEconomicReport(report.id)).then((res) => {
      if (res.ok) {
        openAlert(
          'success',
          t('reports:economic.delete.success.title'),
          t('reports:economic.delete.success.message')
        )
        load()
      }

      setDeleting(false)
      setConfirmDelete(false)
    })
  }

  const statusLabel = t(`reports:economic.list.status.${report.status}`, report.status)
  const statusClass =
    {
      billing_pending: 'badge-light-primary',
      completed: 'badge-light-success',
      completed_with_warnings: 'badge-light-warning',
      error: 'badge-light-danger',
      scheduled: 'badge-light-primary',
      processing_files: 'badge-light-info',
      refund_pending: 'badge-light-warning',
      calculating: 'badge-light-info',
      generating_map: 'badge-light-info',
      generating_pdf: 'badge-light-info',
    }[report.status] || 'badge-light-secondary'

  const deleteAction = canDeleteFromList && (
    <span className='report-item__delete-flow' onClick={(event) => event.stopPropagation()}>
      {!confirmDelete && (
        <button
          type='button'
          className='btn btn-sm btn-light-danger report-item__delete'
          onClick={requestDelete}
          disabled={deleting}
        >
          {t('reports:economic.delete.button')}
        </button>
      )}
      {confirmDelete && (
        <span className='report-item__delete-confirmation'>
          <button
            type='button'
            className='btn btn-sm btn-danger report-item__delete-confirm'
            onClick={deleteReport}
            disabled={deleting}
          >
            {t('reports:economic.delete.yes_button')}
          </button>
          <button
            type='button'
            className='btn btn-sm btn-light report-item__delete-cancel'
            onClick={cancelDelete}
            disabled={deleting}
          >
            {t('reports:economic.delete.no_button')}
          </button>
        </span>
      )}
    </span>
  )

  if (isMobile) {
    return (
      <div
        role='button'
        tabIndex={0}
        onClick={onClick}
        onKeyDown={onKeyDown}
        className='reports-mobile-report-card'
      >
        <span className='reports-mobile-report-card__bar' />
        <span className='reports-mobile-report-card__copy'>
          <span className='reports-mobile-report-card__title'>{report.name}</span>
          <span className={`badge ${statusClass} reports-mobile-report-card__badge`.trim()}>
            {statusLabel}
          </span>
        </span>
        {deleteAction}
        {canOpenReport && (
          <span className='reports-mobile-report-card__action'>
            <KTSVG path='/media/icons/duotune/arrows/arr024.svg' className='svg-icon svg-icon-2' />
          </span>
        )}
      </div>
    )
  }

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      className='d-flex report-item align-items-center p-5'
    >
      <span className='bullet bullet-vertical h-40px bg-primary me-5 report-item__bullet'></span>
      <div className='flex-grow-1 report-item__content'>
        <span className='text-gray-800 text-hover-primary fw-bold fs-6 report-item__title'>
          {report.name}
        </span>
        <p className='p-0 d-block report-item__status'>
          <span className={`badge ${statusClass}`.trim()}>{statusLabel}</span>
        </p>
      </div>
      {deleteAction}
      {canOpenReport && (
        <span className='btn btn-sm btn-light-primary fs-8 fw-bold report-item__cta'>
          <KTSVG path='/media/icons/duotune/arrows/arr024.svg' />
        </span>
      )}
    </div>
  )
}

export default Report
